import {defineStore} from 'pinia'
import {ref, reactive, computed, nextTick} from "vue"
import useLayoutStore from "@/stores/layoutStoreStore"
import router from "@/router";
import {ROUTE_NAMES} from "@/router"
import Supabase from "@/plugins/Supabase";
import useDeliveryStore from "@/stores/deliveryStore";
import {selectMealExtraGroups} from "@/utils/mealExtraGroups";
import {
    isCategoryEnabledForCurrentMenu,
    isCategoryVisibleForDeliveryType
} from "@/utils/categoryVisibility";


const useContentStore = defineStore('contentStore', () => {

    const deliveryStore = useDeliveryStore()

    const appContentData = ref([])
    const appContentLoading = ref(true)
    const loadAppContent = async () => {

        const appContentRequest = Supabase
            .from('v2_Hajcer_App_Categories')
            .select(`
        *,
        items:v2_Hajcer_App_Products (
            *,
            pluItem:v2_Hajcer_PluItems!v2_Hajcer_App_Products_pluItemId_fkey (* ),
            foodPackaging:v2_Hajcer_PluItems!v2_Hajcer_App_Products_foodPackaging_fkey (*  )
        ),
        mealExtra1:v2_Hajcer_App_Products_Extra!v2_Hajcer_App_Categories_mealExtraId_1_restaurantPlace_fkey (
           *,
            products:v2_Hajcer_App_Products_Extra_Products (
                *,
                product:v2_Hajcer_App_Products (
                    *,
                    pluItem:v2_Hajcer_PluItems!v2_Hajcer_App_Products_pluItemId_fkey (*  ),
                    foodPackaging:v2_Hajcer_PluItems!v2_Hajcer_App_Products_foodPackaging_fkey (* ),
                    deposit:v2_Hajcer_PluItems!v2_Hajcer_App_Products_depositId_fkey (* )
                )
            )
        ),
        mealExtra2:v2_Hajcer_App_Products_Extra!v2_Hajcer_App_Categories_mealExtraId_2_restaurantPlace_fkey (
            *,
            products:v2_Hajcer_App_Products_Extra_Products (
                *,
                product:v2_Hajcer_App_Products (
                    *,
                    pluItem:v2_Hajcer_PluItems!v2_Hajcer_App_Products_pluItemId_fkey (*  ),
                    foodPackaging:v2_Hajcer_PluItems!v2_Hajcer_App_Products_foodPackaging_fkey (* ),
                    deposit:v2_Hajcer_PluItems!v2_Hajcer_App_Products_depositId_fkey (* )
                )
            )
        ),
        mealExtra3:v2_Hajcer_App_Products_Extra!v2_Hajcer_App_Categories_mealExtraId_3_restaurantPlace_fkey (
           *,
            products:v2_Hajcer_App_Products_Extra_Products (
                *,
                product:v2_Hajcer_App_Products (
                    *,
                    pluItem:v2_Hajcer_PluItems!v2_Hajcer_App_Products_pluItemId_fkey (*  ),
                    foodPackaging:v2_Hajcer_PluItems!v2_Hajcer_App_Products_foodPackaging_fkey (* ),
                    deposit:v2_Hajcer_PluItems!v2_Hajcer_App_Products_depositId_fkey (* )
                )
            )
        )
    `)
            .order('order', {ascending: true});

        const tableMealExtraRequest = Supabase
            .from('v2_Hajcer_App_Categories')
            .select(`
                id,
                restaurantPlace,
                tableMealExtraId_1,
                tableMealExtraId_2,
                tableMealExtraId_3,
                tableMealExtra1:v2_Hajcer_App_Products_Extra!v2_Hajcer_App_Categories_tableMealExtraId_1_fkey (
                    *,
                    products:v2_Hajcer_App_Products_Extra_Products (
                        *,
                        product:v2_Hajcer_App_Products (
                            *,
                            pluItem:v2_Hajcer_PluItems!v2_Hajcer_App_Products_pluItemId_fkey (*),
                            foodPackaging:v2_Hajcer_PluItems!v2_Hajcer_App_Products_foodPackaging_fkey (*),
                            deposit:v2_Hajcer_PluItems!v2_Hajcer_App_Products_depositId_fkey (*)
                        )
                    )
                ),
                tableMealExtra2:v2_Hajcer_App_Products_Extra!v2_Hajcer_App_Categories_tableMealExtraId_2_fkey (
                    *,
                    products:v2_Hajcer_App_Products_Extra_Products (
                        *,
                        product:v2_Hajcer_App_Products (
                            *,
                            pluItem:v2_Hajcer_PluItems!v2_Hajcer_App_Products_pluItemId_fkey (*),
                            foodPackaging:v2_Hajcer_PluItems!v2_Hajcer_App_Products_foodPackaging_fkey (*),
                            deposit:v2_Hajcer_PluItems!v2_Hajcer_App_Products_depositId_fkey (*)
                        )
                    )
                ),
                tableMealExtra3:v2_Hajcer_App_Products_Extra!v2_Hajcer_App_Categories_tableMealExtraId_3_fkey (
                    *,
                    products:v2_Hajcer_App_Products_Extra_Products (
                        *,
                        product:v2_Hajcer_App_Products (
                            *,
                            pluItem:v2_Hajcer_PluItems!v2_Hajcer_App_Products_pluItemId_fkey (*),
                            foodPackaging:v2_Hajcer_PluItems!v2_Hajcer_App_Products_foodPackaging_fkey (*),
                            deposit:v2_Hajcer_PluItems!v2_Hajcer_App_Products_depositId_fkey (*)
                        )
                    )
                )
            `);

        const [
            {data: appContent, error},
            {data: tableMealExtraCategories, error: tableMealExtraError}
        ] = await Promise.all([appContentRequest, tableMealExtraRequest])

        if (error) {
            alert('Error loading app content')
            console.error('Error loading app content', error)
            appContentData.value = []
            appContentLoading.value = false
            return
        }

        let mappedAppContent = appContent ?? []

        if (!tableMealExtraError && tableMealExtraCategories) {
            const tableMealExtraByCategory = new Map(
                tableMealExtraCategories.map(category => [
                    `${category.id}:${category.restaurantPlace}`,
                    category
                ])
            )
            mappedAppContent = mappedAppContent.map(category => ({
                ...category,
                ...tableMealExtraByCategory.get(`${category.id}:${category.restaurantPlace}`)
            }))
        } else {
            // The migration may not be deployed yet. Removing the new ID fields
            // makes the selector intentionally fall back to the original groups.
            console.warn('Table-order meal extras are unavailable; using original groups.', tableMealExtraError)
            mappedAppContent = mappedAppContent.map(category => {
                const legacyCategory = {...category}
                delete legacyCategory.tableMealExtraId_1
                delete legacyCategory.tableMealExtraId_2
                delete legacyCategory.tableMealExtraId_3
                return legacyCategory
            })
        }

        console.log('[!!]', {appContent: mappedAppContent, error, tableMealExtraError})

        appContentData.value = mappedAppContent
        console.log('categories.value', categories.value)

        await loadBurgerFlyParts()
        appContentLoading.value = false


        // // map cateogires
        // const mappedCategories = appContent.filter(c => c.restaurantPlace === deliveryStore.deliveryPlaceSymbol).map(category => {
        //     return {
        //         ...category
        //     }
        // })
        //
        // categories.value = mappedCategories
        // await setSelectedCategoryId(categories.value[0].id)
        //
        // console.log('categories.value', categories.value)
        //
        // appContentLoading.value = false

    }

    const mapContentMapped = ref(false)
    const mapAppContent = async () => {
        console.log('mapAppContent()', deliveryStore.deliveryPlaceSymbol)
        const mappedCategories = appContentData.value
            .filter(category => isCategoryEnabledForCurrentMenu(category))
            .filter(category => category.restaurantPlace === deliveryStore.deliveryPlaceSymbol)
            .filter(category => isCategoryVisibleForDeliveryType(
                category,
                deliveryStore.selectedDeliveryType
            ))
            .map(category => ({...category}))

        categories.value = mappedCategories
        const firstCategoryId = categories.value[0]?.id ?? null

        if (firstCategoryId) {
            await setSelectedCategoryId(firstCategoryId)
        } else {
            _selectedCategoryId.value = null
            itemsInCategory.value = []
            layoutStore.closeSideMenu()
            if (router.currentRoute.value.name !== ROUTE_NAMES.HOME_VIEW) {
                await router.push({name: ROUTE_NAMES.HOME_VIEW})
            }
        }

        mapContentMapped.value = true
    }

    const layoutStore = useLayoutStore()
    /*
     * Categories
     */
    const _selectedCategoryId = ref(null)
    const selectedCategoryId = computed(() => _selectedCategoryId.value)
    _selectedCategoryId.value = 1

    const setSelectedCategoryId = async (id) => {
        _selectedCategoryId.value = id
        layoutStore.closeSideMenu()
        await router.push({name: ROUTE_NAMES.HOME_VIEW})
    }

    const itemsInCategory = ref([])
    const loadCategoryData = async () => {
        itemsInCategory.value = []
        await nextTick()

        if (selectedCategory.value?.items) {
            itemsInCategory.value = [...selectedCategory.value.items]
            return
        }

        // itemsInCategory.value = Array.from({length: 12}, (_, i) => i)
    }

    const categories = ref([])
    // const categories = ref([...x])

    const selectedCategory = computed(() => categories.value.find(category => category.id === selectedCategoryId.value))
    const selectedMealExtraGroups = computed(() =>
        selectMealExtraGroups(selectedCategory.value, deliveryStore.isTableOrder)
    )
    const selectedCategoryIndex = computed(() => categories.value.findIndex(category => category.id === selectedCategoryId.value))
    const nextCategoryId = computed(() => {
        const index = categories.value.findIndex(category => category.id === selectedCategoryId.value)
        return categories.value[index + 1]?.id ?? null
    })
    const prevCategoryId = computed(() => {
        const index = categories.value.findIndex(category => category.id === selectedCategoryId.value)
        return categories.value[index - 1]?.id ?? null
    })
    const isSelectedFirstCategory = computed(() => selectedCategoryId.value === categories.value[0]?.id)

    const burgerFlyParts = ref({})
    // const burgerFlyParts = ref([])
    const loadBurgerFlyParts = async () => {
        const {data, error} = await Supabase.from('v2_Hajcer_App_Products_Fly_Parts').select('*')

        // burgerFlyParts.value = data
        data.forEach(part => {
            burgerFlyParts.value[part.id] = part
        })

        console.log('burgerFlyParts burgerFlyParts', burgerFlyParts.value)
    }

    return {
        loadAppContent,
        categories,
        burgerFlyParts,
        selectedCategoryId,
        selectedCategory,
        selectedMealExtraGroups,
        isSelectedFirstCategory,
        nextCategoryId,
        prevCategoryId,
        selectedCategoryIndex,
        itemsInCategory,
        setSelectedCategoryId,
        loadCategoryData,
        appContentLoading,
        mapContentMapped,
        mapAppContent
    }


})

export default useContentStore
